import React, { type Dispatch, type SetStateAction, type PropsWithChildren } from 'react'

import { Box, Paper, Typography } from '@mui/material'
import { DownloadCsvButton, DownloadExcelButton, UploadButton } from 'components/commonparts'
import { useStock } from 'hooks/useStock'

import type { ResponseResult, GetStockStockGetRequest } from 'api-client'

interface StockDetailFileDownloadUploadProps {
  // Search conditions (I - Input from parent)
  trCompanyId?: number | null | undefined
  centerIdList?: number[]
  orgCenterIdList?: string[]
  orgSupplierId?: string | null | undefined
  itemCode?: string | null | undefined
  itemCodes?: string[]
  orgSupplierIds?: string[]
  dateRangeStartDate?: Date | null | undefined
  dateRangeEndDate?: Date | null | undefined
  searchWeekNum?: number | null | undefined
  
  // Output events (O - Output to parent)
  setResultMessage: Dispatch<SetStateAction<ResponseResult | undefined>>
  onStockFileUploadEnd?: () => void
  onCsvDownload?: () => void
  onExcelDownload?: () => void
  
  // Loading states
  isCsvLoading?: boolean
  isExcelLoading?: boolean
  isUploadLoading?: boolean
  
  
  // Component configuration
  title?: string
  showUpload?: boolean
  showDownload?: boolean
  requireCenter?: boolean
}

function StockDetailFileDownloadUpload({
  trCompanyId,
  centerIdList,
  orgCenterIdList,
  orgSupplierId,
  itemCode,
  itemCodes,
  orgSupplierIds,
  dateRangeStartDate,
  dateRangeEndDate,
  searchWeekNum,
  setResultMessage,
  onStockFileUploadEnd,
  onCsvDownload,
  onExcelDownload,
  isCsvLoading = false,
  isExcelLoading = false,
  isUploadLoading = false,
  title = '入出荷在庫明細',
  showUpload = true,
  showDownload = true,
  requireCenter = false,
  children,
}: PropsWithChildren<StockDetailFileDownloadUploadProps>) {
  const {
    fetchStockUploadFile,
    isLoading: stockUploadIsLoading,
    fetchStockCsvDownload,
    isCsvLoading: csvDownloadLoading,
    fetchStockXlsxDownload,
    isXlsxLoading: xlsxDownloadLoading,
  } = useStock()

  // Disable download when none of Center, Supplier, Item is selected
  const hasCenter = (centerIdList?.length ?? 0) > 0 || (orgCenterIdList?.length ?? 0) > 0
  const hasSupplier = (orgSupplierId != null && orgSupplierId !== '') || (orgSupplierIds?.length ?? 0) > 0
  const hasItem = (itemCode != null && itemCode !== '') || (itemCodes?.length ?? 0) > 0
  const isDownloadDisabled = !hasCenter && !hasSupplier && !hasItem

  const handleCsvDownload = async () => {
    try {
      if (isDownloadDisabled) return
      // Build search parameters for download
      const searchParams: GetStockStockGetRequest = {
        companyId: trCompanyId ?? 0,
        centerIds: centerIdList,
        orgSupplierId: !orgSupplierIds?.length ? (orgSupplierId || undefined) : undefined,
        orgSupplierIds: orgSupplierIds?.length ? orgSupplierIds : undefined,
        itemCode: !itemCodes?.length ? (itemCode || undefined) : undefined,
        itemCodes: itemCodes?.length ? itemCodes : undefined,
        startDate: dateRangeStartDate || undefined,
        endDate : dateRangeEndDate  || undefined,
      }
      
      const filename = '入出荷在庫明細'
      const errorResult = await fetchStockCsvDownload(filename, searchParams)
      if (errorResult) {
        setResultMessage(errorResult)
        return
      }
      onCsvDownload?.()
    } catch (error) {
      setResultMessage({
        status: 'error',
        message: 'CSVファイルのダウンロードでエラーが発生しました',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
    }
  }

  const handleExcelDownload = async () => {
    try {
      if (isDownloadDisabled) return
      // Build search parameters for download
      const searchParams: GetStockStockGetRequest = {
        companyId: trCompanyId ?? 0,
        centerIds: centerIdList?.length ? centerIdList : undefined,
        orgSupplierId: !orgSupplierIds?.length ? (orgSupplierId || undefined) : undefined,
        orgSupplierIds: orgSupplierIds?.length ? orgSupplierIds : undefined,
        itemCode: !itemCodes?.length ? (itemCode || undefined) : undefined,
        itemCodes: itemCodes?.length ? itemCodes : undefined,
        startDate: dateRangeStartDate || undefined,
        endDate: dateRangeEndDate  || undefined,
      }
      
      const filename = '入出荷在庫明細'
      const errorResult = await fetchStockXlsxDownload(filename, searchParams)
      if (errorResult) {
        setResultMessage(errorResult)
        return
      }
      onExcelDownload?.()
    } catch (error) {
      setResultMessage({
        status: 'error',
        message: 'Excelファイルのダウンロードでエラーが発生しました',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
    }
  }

  const handleFileUpload = async (file: File) => {
    try {
      const response = await fetchStockUploadFile({ postedFile: file })
      if (response?.result.status === 'success') {
        // Call the upload completion callback
        onStockFileUploadEnd?.()
        
        setResultMessage({
          status: 'success',
          message: 'ファイルのアップロードが完了しました',
          systemDate: null,
          pageNo: 0,
          systemInfo: null,
        })
      } else {
        setResultMessage(response?.result)
      }
      setResultMessage(response?.result)
    } catch (e) {
      setResultMessage(e as ResponseResult)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography component="div" variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        {title}
      </Typography>
      
      {/* Search Conditions (children) */}
      {children}
      
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center">
        {showDownload && (
          <Box display="flex">
            <DownloadCsvButton onClick={handleCsvDownload} isLoading={isCsvLoading || csvDownloadLoading} disabled={isDownloadDisabled} />
            <DownloadExcelButton onClick={handleExcelDownload} isLoading={isExcelLoading || xlsxDownloadLoading} disabled={isDownloadDisabled} />
          </Box>
        )}

        {showUpload && (
          <Box>
            <UploadButton
              onClick={handleFileUpload}
              isLoading={isUploadLoading || stockUploadIsLoading}
              placeholder="ファイルをアップロードして登録"
              controlID="stock-detail-file-upload"
            />
          </Box>
        )}
      </Box>

    </Paper>
  )
}

export default StockDetailFileDownloadUpload
